import { useDispatch, useSelector } from "react-redux";

import { Box, Button, Stack, Typography } from "@mui/material";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { DialogType, RegistrationInPortfolioErrors } from "@interfaces/slices";

import WarningCheck from "@assets/download_warning_icon.svg";
import { RootState, store } from "@store/store";
import { useEffect, useState } from "react";

interface ErroresAltaCartera extends RegistrationInPortfolioErrors {}

interface ErrorAltaCartera {
    label?: string;
    msg: string;
}

const toText = (value: unknown): string => (typeof value === "string" ? value : JSON.stringify(value));

interface ProviderStatusItem {
    NombreCliente?: string;
    Detalle?: unknown;
}

const collectDataDetails = (data: unknown): Array<ErrorAltaCartera> => {
    if (!data || typeof data !== "object") return [];
    return Object.values(data as Record<string, unknown>).flatMap((value) =>
        Array.isArray(value)
            ? (value as Array<ProviderStatusItem | null>)
                  .filter((item): item is ProviderStatusItem => !!item && typeof item === "object" && !!item.Detalle)
                  .map((item) => ({ label: item.NombreCliente || "Detalle", msg: toText(item.Detalle) }))
            : [],
    );
};

const normalizeErrors = (errors: unknown): Array<ErrorAltaCartera> => {
    if (Array.isArray(errors)) {
        return errors.map((error) => {
            if (typeof error === "string") return { msg: error };
            return {
                label: Array.isArray(error?.loc) ? error.loc.join(", ") : undefined,
                msg: toText(error?.msg ?? error?.message ?? error),
            };
        });
    }
    if (typeof errors === "string") return errors ? [{ msg: errors }] : [];
    if (errors && typeof errors === "object") {
        const {
            message,
            detalle,
            errors: nestedErrors,
            data,
        } = errors as { message?: unknown; detalle?: unknown; errors?: unknown; data?: unknown };
        const nestedList = normalizeErrors(nestedErrors);
        if (nestedList.length) return nestedList;

        const list: Array<ErrorAltaCartera> = [];
        if (message) list.push({ msg: toText(message) });
        if (detalle) list.push({ label: "Detalle", msg: toText(detalle) });
        list.push(...collectDataDetails(data));
        return list.length ? list : [{ msg: toText(errors) }];
    }
    return [];
};

export const ErroresAltaCartera = (props: ErroresAltaCartera) => {
    const dispatch = useDispatch();

    const [errors, setErrors] = useState<Array<ErrorAltaCartera>>([]);

    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);

    const downloadData = () => {
        const blob = new Blob([JSON.stringify(props.data)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `integración_cartera_solicitud-${applicationSelected.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        setErrors(normalizeErrors(props.errors));
    }, [props]);

    return (
        <Box sx={{
            margin: 4
        }}>
            <Box sx={{
                textAlign: "center"
            }}>
                <img src={WarningCheck} height={"65px"} />
            </Box>
            <Typography
                variant="body1"
                sx={{
                    color: "main3",
                    fontWeight: 400,
                    textAlign: "center",
                    mb: 2
                }}>
                Se identificaron los siguientes errores al realizar el alta en cartera:
            </Typography>
            <Stack sx={{
                rowGap: 1
            }}>
                {errors.length === 0 && (
                    <Typography variant="body1" sx={{
                        color: "main3",
                        fontWeight: 500,
                        textAlign: "center"
                    }}>
                        No se recibió el detalle del error.
                    </Typography>
                )}
                {errors.map((error, index) => (
                    <Stack key={index} direction={"row"} sx={{
                        columnGap: 0.5
                    }}>
                        {error.label && (
                            <Typography variant="body1" sx={{
                                color: "main3",
                                fontWeight: 400
                            }}>
                                {error.label}:
                            </Typography>
                        )}
                        <Typography variant="body1" sx={{
                            color: "main3",
                            fontWeight: 500
                        }}>
                            {error.msg}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
            <Stack
                direction={"row"}
                sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    mt: 3
                }}>
                <Button variant="contained" color="secondary4" onClick={downloadData}>
                    Descargar JSON
                </Button>
                <Button variant="contained" color="main2" onClick={() => dispatch(setDialogConfig())}>
                    Cerrar
                </Button>
            </Stack>
        </Box>
    );
};
